import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { User, AuthCheckResponse } from '../types';
import { authService } from '../services/api';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  passwordResetRequired: boolean;
  rememberMe: boolean;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<any>;
  register: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  handlePasswordChanged: () => Promise<void>;
  checkPasswordResetRequired: (user: User) => boolean;
  setAuthenticatedUser: (user: User) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [passwordResetRequired, setPasswordResetRequired] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const authCheckInProgressRef = useRef(false);

  const checkPasswordResetRequired = (user: User): boolean => {
    return user.passwordResetRequired === true;
  };

  const checkAuthStatus = useCallback(async () => {
    // Prevent duplicate calls (important for React StrictMode)
    if (authCheckInProgressRef.current) {
      return;
    }
    
    authCheckInProgressRef.current = true;
    try {
      const response: AuthCheckResponse = await authService.checkAuth();
      setUser(response.user);
      setPasswordResetRequired(checkPasswordResetRequired(response.user));
      setRememberMe(response.sessionInfo?.rememberMe || false);
    } catch (error) {
      // Not authenticated - this is expected on login page
      setUser(null);
      setPasswordResetRequired(false);
      setRememberMe(false);
    } finally {
      setLoading(false);
      authCheckInProgressRef.current = false;
    }
  }, []); // Empty dependency array - this function should only be created once

  useEffect(() => {
    checkAuthStatus();
  }, [checkAuthStatus]);

  const login = async (email: string, password: string, rememberMe?: boolean) => {
    const response = await authService.login(email, password, rememberMe);
    
    // If MFA is required, return the response for the login component to handle
    if (response.requiresMfa) {
      return response;
    }
    
    // Standard login flow - set user immediately
    if (response.user) {
      setUser(response.user);
      setPasswordResetRequired(checkPasswordResetRequired(response.user));
    }
    
    // Prevent checkAuthStatus from overriding the login response
    authCheckInProgressRef.current = false;
    return response;
  };

  const setAuthenticatedUser = (user: User) => {
    setUser(user);
    setPasswordResetRequired(checkPasswordResetRequired(user));
    authCheckInProgressRef.current = false;
  };

  const register = async (email: string, password: string) => {
    const response = await authService.register(email, password);
    setUser(response.user);
    setPasswordResetRequired(checkPasswordResetRequired(response.user));
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout API error (will still redirect):', error);
    }
    // Always clear user state and redirect, even if API call fails
    setUser(null);
    setPasswordResetRequired(false);
    setRememberMe(false);
    // Force redirect to login page
    window.location.href = '/login';
  };

  const handlePasswordChanged = async () => {
    // Refresh user data after password change to get updated flags
    // Prevent duplicate calls if already in progress
    if (authCheckInProgressRef.current) {
      return;
    }
    
    authCheckInProgressRef.current = true;
    try {
      const response: AuthCheckResponse = await authService.checkAuth();
      setUser(response.user);
      setPasswordResetRequired(checkPasswordResetRequired(response.user));
      setRememberMe(response.sessionInfo?.rememberMe || false);
    } catch (error) {
      console.error('Failed to refresh user data after password change:', error);
    } finally {
      authCheckInProgressRef.current = false;
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    passwordResetRequired,
    rememberMe,
    login,
    register,
    logout,
    handlePasswordChanged,
    checkPasswordResetRequired,
    setAuthenticatedUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 